import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { Candidate } from '../../lib/db/types';
import { DISPOSITIONS, ISSUE_TAGS, PITCH_SCRIPT, SUPPORT_LEVELS } from '../../lib/ui/support';

export function SupportButtons({ value, onChange }: { value: number | null; onChange: (v: number) => void }) {
  return (
    <div className="support" role="group" aria-label="Support level">
      {SUPPORT_LEVELS.map((s) => (
        <button
          key={s.value}
          type="button"
          className={s.cls}
          aria-pressed={value === s.value}
          aria-label={s.label}
          onClick={() => onChange(s.value)}
        >
          {s.value}
          <small>{s.short}</small>
        </button>
      ))}
    </div>
  );
}

export function CandidateChips({
  candidates,
  selected,
  onToggle,
}: {
  candidates: Candidate[];
  selected: string[];
  onToggle: (slug: string) => void;
}) {
  return (
    <div className="chips" role="group" aria-label="Candidate resonance">
      {candidates.map((c) => (
        <button
          key={c.id}
          type="button"
          className="chip"
          aria-pressed={selected.includes(c.nation_slug)}
          style={{ '--chip': c.colour } as CSSProperties}
          onClick={() => onToggle(c.nation_slug)}
        >
          <span className="swatch" aria-hidden />
          {c.name.split(' ')[0]}
          <span className="check" aria-hidden>
            ✓
          </span>
        </button>
      ))}
    </div>
  );
}

export function IssueChips({ selected, onToggle }: { selected: string[]; onToggle: (t: string) => void }) {
  return (
    <div className="chips" role="group" aria-label="Top issues">
      {ISSUE_TAGS.map((t) => (
        <button key={t} type="button" className="chip" aria-pressed={selected.includes(t)} onClick={() => onToggle(t)}>
          {t}
          <span className="check" aria-hidden>
            ✓
          </span>
        </button>
      ))}
    </div>
  );
}

/** Always-visible Not-home (1-tap terminal) + a More control for other dispositions. */
export function DispositionRow({ onPick, onMore }: { onPick: (d: string) => void; onMore: () => void }) {
  return (
    <div className="dispo">
      <button type="button" className="nothome" onClick={() => onPick('not_home')}>
        Not home
      </button>
      <button type="button" className="btn more" onClick={onMore}>
        More ▾
      </button>
    </div>
  );
}

export function MoreDispositions({ onPick, onClose }: { onPick: (d: string) => void; onClose: () => void }) {
  return (
    <div className="col" style={{ gap: 8, marginTop: 8 }}>
      {DISPOSITIONS.filter((d) => !d.primary).map((d) => (
        <button key={d.value} type="button" className="btn btn-block" onClick={() => onPick(d.value)}>
          {d.label}
        </button>
      ))}
      <button type="button" className="btn btn-block" onClick={onClose}>
        Cancel
      </button>
    </div>
  );
}

export function PitchHelper({ leanHint }: { leanHint?: string | null }) {
  return (
    <details className="collapse card" style={{ margin: '12px 0' }}>
      <summary>📣 Pitch &amp; consent</summary>
      <p style={{ marginTop: 8 }}>{PITCH_SCRIPT}</p>
      {leanHint && (
        <p className="badge ok" style={{ display: 'inline-block' }}>
          Known lean: {leanHint} — try the group cross-sell.
        </p>
      )}
    </details>
  );
}

/** Web Speech voice-to-text for the note. Renders nothing where unsupported. */
export function VoiceNoteButton({ onText }: { onText: (t: string) => void }) {
  const [listening, setListening] = useState(false);
  const recRef = useRef<unknown>(null);
  const SR =
    typeof window !== 'undefined'
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)
      : null;

  useEffect(() => () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (recRef.current as any)?.stop?.();
    } catch {
      /* ignore */
    }
  }, []);

  if (!SR) return null;

  const start = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rec: any = new SR();
    recRef.current = rec;
    rec.lang = 'en-CA';
    rec.interimResults = false;
    rec.onresult = (e: { results: { 0: { 0: { transcript: string } } } }) => onText(e.results[0][0].transcript);
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    setListening(true);
    rec.start();
  };

  return (
    <button type="button" className="btn" onClick={start} aria-pressed={listening}>
      {listening ? '● Listening…' : '🎤 Speak note'}
    </button>
  );
}

export function Toast({
  message,
  actionLabel,
  onAction,
}: {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="toast" role="status">
      <span>{message}</span>
      {actionLabel && onAction && (
        <button type="button" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}
